import { useReactTable } from "@tanstack/react-table";
import React from "react";
import type { NotificationSummary } from "@aidkitorg/aidkit/lib/notifications/NotificationService";
import { Button, Checkbox, MultiSelect } from "@aidkitorg/component-library";
import { SelectOptionType } from "@aidkitorg/component-library/dist/types/src/Select";
import { useLocalizedStrings } from "../../../Localization";
import type { NotificationsOverview } from "@aidkitorg/types/lib/survey";

export type NotificationsFilterProps = {
  table: ReturnType<typeof useReactTable<NotificationSummary>>
  setActiveFilters: React.Dispatch<React.SetStateAction<number>>
  filters: Filters,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  defaultTableColumns?: NotificationsOverview['defaultTableColumns']
}

export type Filters = {
  enabledStatus: string[],
  channels: string[],
  firstSentStart: string,
  firstSentEnd: string,
  lastSentStart: string,
  lastSentEnd: string,
}

export const NotificationsFilter: React.FC<NotificationsFilterProps> = ({
  table,
  setActiveFilters,
  filters,
  setFilters,
  defaultTableColumns
}) => {
  const L = useLocalizedStrings()

  // Determine which filters to show based on config
  const showStatusFilter = defaultTableColumns?.status === 'visibleAndFilterable';
  const showChannelFilter = defaultTableColumns?.channels === 'visibleAndFilterable';
  const showFirstSentFilter = defaultTableColumns?.firstSent === 'visibleAndFilterable';
  const showLastSentFilter = defaultTableColumns?.lastSent === 'visibleAndFilterable';
  
  const applyFilters = () => {
    let activeFilters = 0;

    const enabledStatusCol = table.getColumn("enabledStatus")

    if (filters.enabledStatus.length > 0) activeFilters++
    enabledStatusCol?.setFilterValue(filters.enabledStatus.length > 0 ? filters.enabledStatus : undefined)

    const channelsCol = table.getColumn("methods")

    if (filters.channels.length > 0) activeFilters++
    channelsCol?.setFilterValue(filters.channels.length > 0 ? filters.channels : undefined)

    const firstSentCol = table.getColumn("firstSent")

    if (filters.firstSentStart !== "" || filters.firstSentEnd !== "") activeFilters++
    firstSentCol?.setFilterValue(
      filters.firstSentStart || filters.firstSentEnd
        ? { firstSent: filters.firstSentStart, lastSent: filters.firstSentEnd }
        : undefined
    )

    const lastSentCol = table.getColumn("lastSent")

    if (filters.lastSentStart !== "" || filters.lastSentEnd !== "") activeFilters++
    lastSentCol?.setFilterValue(
      filters.lastSentStart || filters.lastSentEnd
        ? { firstSent: filters.lastSentStart, lastSent: filters.lastSentEnd }
        : undefined
    )

    setActiveFilters(activeFilters)
  }

  const resetFilters = () => {
    setFilters({
      enabledStatus: [],
      channels: [],
      firstSentStart: "",
      firstSentEnd: "",
      lastSentStart: "",
      lastSentEnd: "",
    })
    setActiveFilters(0)
    table.resetColumnFilters()
  }

  const PAYMENT_STATUS_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "enabled",
      label: L.admin_panel.payments.enabled,
    },
    {
      value: "disabled",
      label: L.admin_panel.payments.disabled
    }
  ]

  const CHANNEL_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "email",
      label: L.user.email,
    },
    {
      value: "sms",
      label: L.user.phone,
    },
    {
      value: "whatsapp",
      label: L.admin_panel.notifications.whatsapp,
    }
  ]

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        {showStatusFilter && (
          <div>
            <MultiSelect
              className="h-10"
              options={PAYMENT_STATUS_FILTER_VALUES}
              description={L.admin_panel.notifications.notification_status_desc}
              label={L.admin_panel.notifications.notification_status}
              labelLocation="above"
              value={filters.enabledStatus.map(status => PAYMENT_STATUS_FILTER_VALUES.find(v => v.value === status)).filter(Boolean) as SelectOptionType[]}
              onChange={(selectedOptions) => {
                setFilters(f => ({...f, enabledStatus: selectedOptions.map(opt => opt.value)}))
              }}
              placeholder={L.admin_panel.notifications.select_status}
            />
          </div>
        )}

        {showChannelFilter && (
          <div>
            <MultiSelect
              className="h-10"
              options={CHANNEL_FILTER_VALUES}
              description={L.admin_panel.notifications.filter_by_channel}
              label={L.admin_panel.notifications.channels}
              labelLocation="above"
              value={filters.channels.map(ch => CHANNEL_FILTER_VALUES.find(v => v.value === ch)).filter(Boolean) as SelectOptionType[]}
              onChange={(selectedOptions) => {
                setFilters(f => ({...f, channels: selectedOptions.map(opt => opt.value)}))
              }}
              placeholder={L.admin_panel.notifications.select_channel}
            />
          </div>
        )}

        {showFirstSentFilter && (
          <div>
            <label className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.notifications.first_sent}</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label={L.admin_panel.notifications.first_sent}
                type="date"
                value={filters.firstSentStart}
                onChange={(e) => setFilters(f => ({ ...f, firstSentStart: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm"
              />
              <span className="text-gray-700">to</span>
              <input
                aria-label={L.admin_panel.notifications.first_sent}
                type="date"
                value={filters.firstSentEnd}
                onChange={(e) => setFilters(f => ({ ...f, firstSentEnd: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm"
              />
            </div>
          </div>
        )}

        {showLastSentFilter && (
          <div>
            <label className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.notifications.last_sent}</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label={L.admin_panel.notifications.last_sent}
                type="date"
                value={filters.lastSentStart}
                onChange={(e) => setFilters(f => ({ ...f, lastSentStart: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
              />
              <span className="text-gray-700">to</span>
              <input
                aria-label={L.admin_panel.notifications.last_sent}
                type="date"
                value={filters.lastSentEnd}
                onChange={(e) => setFilters(f => ({ ...f, lastSentEnd: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
              />
            </div>
          </div>
        )}

      </div>

      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>{L.admin_panel.payments.reset_filters}</Button>
          <Button onClick={applyFilters}>{L.admin_panel.payments.apply_filters}</Button>
        </div>
      </div>
    </div>
  )
}